import { CellDoubleClickedEvent, ColDef, PostSortRowsParams } from "ag-grid-community";
import { RaonColDef } from "src/types/grid";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { RemoveRowHandler, postSortHandler, useRowSelectionHandler, createCellCssClassRules, GridCellConfig, createGridDropDownCommitAfter } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, getRowStyleFlags, IndexParameterCell, onGridReady } from "src/utils/grid/gridStyleUtils";
import { useWMS1140_R01Store } from "./Store_WMS1140_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { defaultWMS1140_R01DetailDto, WMS1140_R01DetailDto } from "./Dto_WMS1140_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { CommonCodeColumn, getCommonCodeColumnOptions } from "src/utils/grid/GridColumnContent";

import { useDetailTableWithItemSearch } from "src/hooks/detail-table/useDetailTableWithItemSearch";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useWMS1140_R01Modal } from "./hooks/useWMS1140_R01Modal";
import { useWMS1140_R01CellChange } from "./hooks/WMS1140_R01CellChange";
import { useCodeSearch } from "src/hooks/useCodeSearch";
import { isSubcontractMaterialInoutClass, normalizeWMS1140DetailRow } from "./Constants_WMS1140_R01";

const COLUMN_STATE_KEY = 'WMS1140_R01-detail-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<WMS1140_R01DetailDto> | null>;
	defaultWMS1140_R01DetailRow: WMS1140_R01DetailDto;
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	handleInsertClick: (e: React.MouseEvent, rowIndex?: number) => void;
}

export const DetailTable_WMS1140_R01 = (props: Props) => {
	const { gridRef, defaultWMS1140_R01DetailRow, clickable, setClickable, handleInsertClick } = props;

	const { 
		editWMS1140_R01,
		WMS1140_R01DetailList, setWMS1140_R01DetailList, 
		selectedWMS1140_R01DetailList, setSelectedWMS1140_R01DetailList, removeSelectedWMS1140_R01DetailList, addSelectedWMS1140_R01DetailList, initSelectedWMS1140_R01DetailList,
		deletedWMS1140_R01DetailList, setDeletedWMS1140_R01DetailList, addDeletedWMS1140_R01DetailList, removeDeletedWMS1140_R01DetailList,
		updateWMS1140_R01DetailList, removeWMS1140_R01DetailList,
		detailColumnState, setDetailColumnState,
		detailScrollTop, setDetailScrollTop,
		originalWMS1140_R01DetailList, setOriginalWMS1140_R01DetailList,
	} = useWMS1140_R01Store();

	const { itemStatusList, unitList } = useCommonCodeStore();
	
	const { setCodeSearchModalProps } = useWMS1140_R01Modal();
	const { searchExactItemCode } = useCodeSearch({});
	
	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const isSubcontractInout = isSubcontractMaterialInoutClass(editWMS1140_R01?.inoutClass);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex', // 행 식별 키
		gridData: WMS1140_R01DetailList,
		defaultGridData: defaultWMS1140_R01DetailRow,
		selectedRow: selectedWMS1140_R01DetailList,
		originalGridData: originalWMS1140_R01DetailList,
		updateRow: (index: number, data: any) => updateWMS1140_R01DetailList(index, data),
	});

	/**
	 * 컬럼 헤더명 매핑
	 * 필드명과 표시할 한글명을 매핑
	 */
	const headerNames: Record<string, string> = {
		rowIndex: "",           // 순번 (빈 문자열)
		itemCd: '품목코드',      // 품목 코드
		itemNm: '품명',         // 품명
		itemSpec: '규격',       // 규격
		unit: '단위',      // 단위
		itemStatus: '상태',		// 상태
		qty: '입고량',	// 입고량
		cost: '단가',	// 단가
		amt: '금액',	// 금액
		lotNo: 'LOT No',	// LOT No
		remark: '비고',	// 비고
	}

	const cssCellConfig: GridCellConfig = useMemo(() => ({
		disabledCells: ['itemNm', 'itemSpec', 'unit', 'lotNo', 'amt', ...(isSubcontractInout ? ['cost'] as const : [])],
		insertedDisabledCells: [''],
		commonDisabledCells: ['itemCd', 'itemStatus', 'unit', 'qty', 'cost'],
		pointerCells: ['cancelQty', 'remark'],
		insertedPointerCells: isSubcontractInout
			? ['itemCd', 'itemNm', 'itemSpec', 'itemStatus', 'unit', 'qty', 'amt']
			: ['itemCd', 'itemNm', 'itemSpec', 'itemStatus', 'unit', 'qty', 'cost', 'amt'],
		commonPointerCells: [''],
		alignLeftCells: [''],
	}), [isSubcontractInout]);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<WMS1140_R01DetailDto>({
			idKey: 'valueIndex',
			selectedList: selectedWMS1140_R01DetailList,
			deletedList: deletedWMS1140_R01DetailList,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules, }};
	}, [
		selectedWMS1140_R01DetailList, deletedWMS1140_R01DetailList,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	]);

	/**
	 * 편집 가능한 컬럼 목록 정의
	 * 사용자가 직접 입력/수정할 수 있는 필드들을 지정
	 */
	const defaultEditableColumns = useMemo(
		() => (isSubcontractInout
			? ['itemCd', 'itemNm', 'itemSpec', 'unit', 'itemStatus', 'qty', 'amt', 'lotNo', 'remark']
			: ['itemCd', 'itemNm', 'itemSpec', 'unit', 'itemStatus', 'qty', 'cost', 'amt', 'lotNo', 'remark']),
		[isSubcontractInout],
	);

	const detailTableHandlers = useWMS1140_R01CellChange({
		WMS1140_R01DetailList,
		originalWMS1140_R01DetailList,
		updateWMS1140_R01DetailList,
		gridRef,
		setCodeSearchModalProps,
		customCd: editWMS1140_R01?.customCd,
		inoutClass: editWMS1140_R01?.inoutClass,
	});

	const handleGridDropDownCommitAfter = useMemo(() => 
		createGridDropDownCommitAfter<WMS1140_R01DetailDto>(defaultEditableColumns),
	[defaultEditableColumns],);

	/** 신규 추가(inserted) 행만 셀 편집 허용 */
	const isInsertedRowEditable = useCallback((params: { data?: WMS1140_R01DetailDto }) => 
		params.data?.inserted === true, [],);

	/** 신규 행이면서 사급자재입고(170)가 아닐 때만 단가 편집 허용 */
	const isCostEditable = useCallback(
		(params: { data?: WMS1140_R01DetailDto }) =>
			isInsertedRowEditable(params) && !isSubcontractInout,
		[isInsertedRowEditable, isSubcontractInout],
	);

	/** LOT 입력 옵션이 Y이고 신규 행인 경우만 편집 허용 */
	const isLotNoEditable = useCallback((params: { data?: WMS1140_R01DetailDto & { lotInputOption?: string } }) =>
		params.data?.lotInputOption === 'Y' && params.data?.inserted === true, [],);

	const baseColumnDefs: RaonColDef<WMS1140_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			editWMS1140_R01?.transportNo === '' ? 
				(params: WMS1140_R01DetailDto) => renderIndexCell(
					params, 
					(innerParams: WMS1140_R01DetailDto) => getRowStyleFlags (
						innerParams,
						{
							deletedList: deletedWMS1140_R01DetailList,
							selectedList: selectedWMS1140_R01DetailList,
							dataList: WMS1140_R01DetailList
						}),
					(rowIndex: number, e: React.MouseEvent) => {
						if (e.ctrlKey) {
							for (let i = 0; i < 10; i++) { handleInsertClick(e, rowIndex); }
						} else { 
							handleInsertClick(e, rowIndex);
						}
					},
					(rowData) => RemoveRowHandler( 
						gridRef,
						WMS1140_R01DetailList, 
						rowData,  // rowData는 renderIndexCell에서 Dto 형식으로 선언함
						setClickable, 
						removeWMS1140_R01DetailList, 
						(valueIndex) => addDeletedWMS1140_R01DetailList([valueIndex.toString()])
					),
				)
			: undefined
		),
		{

			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 120,
			editable: isInsertedRowEditable,
			pasteable: true,
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 250,			
			editable: false,
			pasteable: true,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 200,			
			editable: false,
			pasteable: true,
		},
		{
			headerName: '단위', colId: 'unit', field: 'unit', width: 70,			
			...CommonCodeColumn.options(unitList, false, CommonCodeColumn.change(updateWMS1140_R01DetailList), handleGridDropDownCommitAfter),
			pasteable: true,
		},
		{
			headerName: '상태', colId: 'itemStatus', field: 'itemStatus', width: 60,			
			...CommonCodeColumn.options(itemStatusList, isInsertedRowEditable, CommonCodeColumn.change(updateWMS1140_R01DetailList), handleGridDropDownCommitAfter),
			pasteable: true,
		},
		{
			headerName: '입고량', colId: 'qty', field: 'qty', width: 80,
			editable: isInsertedRowEditable,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		{
			headerName: '단가', colId: 'cost', field: 'cost', width: 65,
			editable: isCostEditable,
			pasteable: !isSubcontractInout,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '금액', colId: 'amt', field: 'amt', width: 96,
			editable:  false,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		{
			headerName: 'LOT No', colId: 'lotNo', field: 'lotNo', width: 130,
			editable: isLotNoEditable,
		},
		// {
		// 	headerName: '출력 여부', colId: 'printed', field: 'printed', width: 90,
		// 	editable: false,
		// 	cellRenderer: (params: any) => params.value ? 'V' : '',
		// 	cellStyle: (params: any) => ({
		// 		textAlign: 'center',
		// 		fontWeight: params.value ? 'bold' : 'normal',
		// 		color: params.value ? '#10b981' : '#6b7280'
		// 	}),
		// },
		// {
		// 	headerName: '비고', colId: 'remark', field: 'remark', width: 150,			
		// 	editable: true,
		// },
		// ...IndexParameterCell(),
	], [deletedWMS1140_R01DetailList, WMS1140_R01DetailList, editWMS1140_R01?.transportNo, handleInsertClick, isInsertedRowEditable, isCostEditable, isLotNoEditable, isSubcontractInout, handleGridDropDownCommitAfter, itemStatusList, unitList, renderIndexCell, selectedWMS1140_R01DetailList, addDeletedWMS1140_R01DetailList, gridRef, removeWMS1140_R01DetailList, setClickable, updateWMS1140_R01DetailList]);

	/** 입고구분 170 선택·조회 시 상세 단가 0 유지 */
	useEffect(() => {
		if (!isSubcontractInout) return;
		const hasNonZeroCost = WMS1140_R01DetailList.some((row) => Number(row.cost) !== 0);
		if (!hasNonZeroCost) return;
		setWMS1140_R01DetailList(
			WMS1140_R01DetailList.map((row) =>
				normalizeWMS1140DetailRow(row, editWMS1140_R01?.inoutClass),
			),
		);
	}, [isSubcontractInout, editWMS1140_R01?.inoutClass]);

	const columnDefs: ColDef<WMS1140_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<WMS1140_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, detailColumnState, headerNames]);


	const handleRowClick = useRowSelectionHandler({
		dataList: WMS1140_R01DetailList,
		idKey: 'valueIndex',
		selectedList: selectedWMS1140_R01DetailList,
		deletedList: deletedWMS1140_R01DetailList,
		addSelected: addSelectedWMS1140_R01DetailList,
		removeSelected: removeSelectedWMS1140_R01DetailList,
		clickable, setClickable
	});

	/**
	 * 품목 검색 기능이 포함된 상세 테이블 이벤트 핸들러 훅
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 * @param itemSearchConfig - 품목 검색 설정
	 * @param constraints - 제약 조건 (notTransport 등)
	 * @param keyDownConfig - 키다운 네비게이션 설정
	 */
	const cellEventHandlers = useDetailTableWithItemSearch<WMS1140_R01DetailDto>({
		detailList: WMS1140_R01DetailList,
		editableColumns: defaultEditableColumns,
		itemSearchConfig: {
			config: {
				category: '품목', // 검색 카테고리
				responseMapping: {
					// 검색 결과 → 테이블 필드 매핑
					itemCd: 'itemCd',      // 품목코드
					itemNm: 'itemNm',      // 품명
					itemSpec: 'itemSpec',  // 규격
					itemUnit: 'unit', // 단위
				},
				clearFields: {
					itemCd: "",
					itemNm: "",
					itemSpec: "",
					unit: "",
					cost: 0,
					amt: 0,
				},
				searchFunction: searchExactItemCode // 품목 검색 함수
			},
			settingCodeSearchModalProps: detailTableHandlers.settingCodeSearchModalProps, // 모달 설정 함수
		},
		constraints: {
			notEdit: (params) => {
				if(params.data?.inserted === true) return true;
				return false;
			}
		},
		keyDownConfig: {
			qtyField: 'qty',
			enableArrowNavigation: true,
		}
	});

	/**
	 * 행 드래그 상태를 추적하는 ref
	 * 드래그 중일 때는 자동 정렬을 방지하여 드래그 효과를 보여줌
	 */
	const isRowDraggingRef = useRef(false);

	const handlePostSort = useCallback((params: PostSortRowsParams<WMS1140_R01DetailDto>) => {
		// 드래그 중에는 postSortRows에 의한 강제 정렬을 막아 드래그가 눈에 보이도록 함
		if (isRowDraggingRef.current) return;
		postSortHandler(params, WMS1140_R01DetailList, originalWMS1140_R01DetailList, setWMS1140_R01DetailList);
	}, [WMS1140_R01DetailList, originalWMS1140_R01DetailList, setWMS1140_R01DetailList]);

	/**
	 * 하단 고정 행 데이터 생성 (합계 행)
	 * 
	 * 기능:
	 * - 수량과 금액의 총합을 계산
	 * - 하단에 고정된 합계 행으로 표시
	 * - 데이터가 없으면 undefined 반환
	 * 
	 * @returns 합계 행 데이터 또는 undefined
	 */
	const pinnedBottomRowData = useMemo(() => {
		// 삭제된 행은 제외하고 수량 합계 계산
		const totalQty : number = WMS1140_R01DetailList
			.reduce((sum, item) => sum + (deletedWMS1140_R01DetailList.includes(item.itemCd) ? 0 : Number(item.qty) || 0), 0);

		// 하단 고정 행 데이터 반환
		return [{
			itemCd: WMS1140_R01DetailList.length.toLocaleString() + "건",
			qty: totalQty,
		}];
	}, [WMS1140_R01DetailList, deletedWMS1140_R01DetailList]);

	return (
		<GridContextWrapper<WMS1140_R01DetailDto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={WMS1140_R01DetailList}
			setPaddedGridData={setWMS1140_R01DetailList}
			initSelected={initSelectedWMS1140_R01DetailList}
			defaultRowData={defaultWMS1140_R01DetailRow}
			baseColumnDefs={columnDefs}	
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<WMS1140_R01DetailDto>
				ref={gridRef}
				rowData={WMS1140_R01DetailList}
				getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}

				disableBuiltinCellSelection={false}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setDetailColumnState}
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}

				rowClassRules={createRowClassRules<WMS1140_R01DetailDto>({
					idKey: 'valueIndex',
					selectedList: selectedWMS1140_R01DetailList,
					deletedList: deletedWMS1140_R01DetailList
				})}

				// 이벤트 핸들러들
				onRowClicked={handleRowClick}                                    
				onCellClicked={cellEventHandlers.handleCellClick}               
				onCellDoubleClicked={cellEventHandlers.handleCellDoubleClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={detailTableHandlers.handleCellChange}
				onCellEditingStopped={detailTableHandlers.handleCellEditingStopped}
				
				// 하단 고정 행 (합계)
				pinnedBottomRowData={pinnedBottomRowData}
				
				// 행 드래그 설정
                rowDragManaged={true}      // 드래그 관리 활성화
                rowDragEntireRow={true}    // 전체 행 드래그
				// 드래그 이벤트 핸들러들
				onRowDragEnter={() => { 
					// 드래그 시작 시 플래그 설정
					isRowDraggingRef.current = true; 
				}}
                onRowDragEnd={(e: any) => {
					/**
					 * 행 드래그 종료 처리
					 * 
					 * 기능:
					 * - 정렬 해제하여 사용자가 정한 순서를 유지
					 * - 드래그된 순서대로 orderIndex 재설정
					 * - 데이터 목록 업데이트
					 */
                    const api = e.api;
                    
                    // 정렬 해제하여 사용자가 정한 순서를 유지
                    if (api.applyColumnState) {
                        api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
                    }
                    
                    // 드래그된 순서대로 데이터 재정렬
                    const reordered: WMS1140_R01DetailDto[] = [];
                    api.forEachNodeAfterFilterAndSort((node: any, idx: number) => {
                        if (!node.data) return;
                        reordered.push({
                            ...node.data,
                            orderIndex: idx + 1,    // 순서 인덱스 재설정
                            orderIndexSeq: 0,       // 시퀀스 초기화
                        });
                    });
                    
                    // 데이터 목록 업데이트
                    setWMS1140_R01DetailList(reordered);
					isRowDraggingRef.current = false; // 드래그 플래그 해제
                }}
				

				// 정렬 후 처리 (드래그 중에는 자동 정렬 방지)
				onPostSortRows={handlePostSort}
			/>
		</GridContextWrapper>
	)
}
